import { useEffect } from "react";
import {
  Printer,
  Download,
  Activity,
  Moon,
  Pill,
  Dna,
  HeartPulse,
  FlaskConical,
  TrendingUp,
  Briefcase,
  Workflow,
  Sparkles,
  ArrowRight,
  Phone,
  Mail,
  Globe,
  Building2,
} from "lucide-react";
import NoderaMark from "@/components/NoderaMark";

/**
 * Print-ready 8.5×11 one-page sales flyer.
 *
 * - Screen view: shows the flyer on an admin shell-aware canvas with print/download CTAs
 * - Print view: @media print rules hide all admin chrome + render exactly the 8.5×11 page
 * - "Download editable PowerPoint" links to /nodera-health-flyer.pptx (generated by
 *   /app/backend/scripts/generate_flyer_pptx.py)
 */
export default function AdminMarketingFlyer() {
  // Inject @media print rules once. Tailwind doesn't ship print utilities by
  // default, and the @page rule must be at document-root level.
  useEffect(() => {
    if (document.getElementById("flyer-print-css")) return;
    const style = document.createElement("style");
    style.id = "flyer-print-css";
    style.textContent = `
      @page {
        size: 8.5in 11in;
        margin: 0;
      }
      @media print {
        body { background: #fff !important; }
        body * { visibility: hidden; }
        #flyer-print-region, #flyer-print-region * { visibility: visible; }
        #flyer-print-region {
          position: absolute !important;
          left: 0 !important; top: 0 !important;
          width: 8.5in !important; min-height: 11in !important;
          margin: 0 !important; padding: 0 !important;
          box-shadow: none !important; border-radius: 0 !important;
          border: none !important;
          page-break-after: avoid;
        }
        .no-print { display: none !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <div data-testid="admin-flyer-page" className="max-w-5xl mx-auto py-8 px-4 space-y-6">
      {/* Toolbar — hidden when printing */}
      <div className="no-print flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
            <Briefcase className="w-3 h-3" /> Sales asset · One-page flyer
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold text-slate-900 tracking-tight">
            Primary care growth flyer
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Print-ready 8.5×11 leave-behind for clinic owners. Click <strong>Print</strong> →
            "Save as PDF" in your browser dialog. For an editable file, download the
            PowerPoint version.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/nodera-health-flyer.pptx"
            download
            data-testid="admin-flyer-download-pptx"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
          >
            <Download className="w-4 h-4" /> Download .pptx
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="admin-flyer-print-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-all shadow-sm hover:shadow-lg"
          >
            <Printer className="w-4 h-4" /> Print → Save as PDF
          </button>
        </div>
      </div>

      {/* === FLYER (the only thing that prints) === */}
      <div
        id="flyer-print-region"
        data-testid="admin-flyer-print-region"
        className="bg-white rounded-2xl border border-slate-200 shadow-xl mx-auto overflow-hidden"
        style={{ width: "8.5in", minHeight: "11in" }}
      >
        {/* Accent bar */}
        <div className="h-[6px] bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600" />

        <div className="px-9 pt-7 pb-9 font-sans text-slate-900">
          {/* Brand bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex w-7 h-7 items-center justify-center text-[#1F4853]">
                <NoderaMark size={24} />
              </span>
              <span className="font-display font-bold text-base tracking-tight text-slate-900">
                Nodera <span className="text-slate-400 font-medium">Health</span>
              </span>
            </div>
            <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-slate-400">
              Operational Infrastructure · Cash-Pay Healthcare
            </div>
          </div>

          {/* Hero */}
          <div className="mt-6">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
              <Sparkles className="w-3 h-3" /> Primary care growth program
            </span>
            <h1 className="mt-3 font-display font-extrabold tracking-tighter text-slate-900 text-[34px] leading-[1.04]">
              Transform Your Clinic Into a Modern{" "}
              <span className="bg-gradient-to-r from-slate-900 via-blue-700 to-slate-900 bg-clip-text text-transparent">
                Consumer Health Platform
              </span>
            </h1>
            <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed max-w-[6.6in]">
              Launch high-demand cash-pay programs without operational headaches —
              turnkey vendor coordination, fulfillment, and program enablement built
              for primary care growth.
            </p>
          </div>

          <Divider />

          {/* Section 1: programs */}
          <SectionHeader label="What we help clinics launch" />
          <div className="grid grid-cols-3 gap-2.5">
            <ProgramCard icon={Pill} label="Weight Loss & GLP-1" />
            <ProgramCard icon={Activity} label="Hormone Optimization" />
            <ProgramCard icon={Moon} label="Sleep Health" />
            <ProgramCard icon={HeartPulse} label="CGM & Remote Monitoring" />
            <ProgramCard icon={Dna} label="Longevity & Wellness" />
            <ProgramCard icon={Sparkles} label="Peptide & Recovery" />
            <ProgramCard icon={FlaskConical} label="Wellness Lab Testing" colSpan={3} />
          </div>

          <Divider />

          {/* Section 2: what we coordinate */}
          <SectionHeader label="What we coordinate" icon={Workflow} />
          <div className="grid grid-cols-3 gap-x-5 gap-y-1.5 text-[11.5px]">
            {[
              "Vendor relationships",
              "Fulfillment workflows",
              "Contracted-rate vendor sourcing",
              "Cash-pay program deployment",
              "Product sourcing coordination",
              "Patient workflow optimization",
              "Program packaging & positioning",
              "Vendor pricing optimization",
              "Operational launch support",
            ].map((line) => (
              <div key={line} className="flex items-start gap-1.5 text-slate-700">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                <span className="leading-snug">{line}</span>
              </div>
            ))}
          </div>

          <Divider />

          {/* Section 3: why clinics work with us */}
          <SectionHeader label="Why clinics work with us" />
          <div className="grid grid-cols-2 gap-2.5">
            <ValueCard
              icon={TrendingUp}
              title="Expand revenue opportunities"
              body="Introduce high-demand consumer services without building complex infrastructure internally."
            />
            <ValueCard
              icon={Workflow}
              title="Simplify vendor management"
              body="We help streamline fulfillment relationships and operational coordination across vendors."
            />
            <ValueCard
              icon={Building2}
              title="Reduce operational lift"
              body="Programs are designed to integrate smoothly into existing clinic workflows."
            />
            <ValueCard
              icon={Sparkles}
              title="Improve margin potential"
              body="Optimized vendor relationships and program structure help support scalable profitability."
            />
          </div>

          <Divider />

          {/* Section 4: ideal partners */}
          <SectionHeader label="Ideal clinic partners" />
          <div className="flex flex-wrap gap-1.5">
            {[
              "Primary Care",
              "Internal Medicine",
              "Family Medicine",
              "Concierge Medicine",
              "Wellness Clinics",
              "Functional Medicine",
              "Multi-Provider Groups",
            ].map((c) => (
              <span
                key={c}
                className="inline-flex items-center text-[10.5px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 py-1"
              >
                {c}
              </span>
            ))}
          </div>

          {/* Section 5: The Opportunity (dark band) */}
          <div className="mt-5 rounded-2xl bg-slate-900 p-5 text-white relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/60 to-transparent" />
            <div className="text-[9.5px] font-bold uppercase tracking-[0.22em] text-blue-300">
              The Opportunity
            </div>
            <p className="mt-2 text-[12.5px] leading-relaxed text-slate-200">
              Consumers increasingly seek proactive, personalized, cash-pay healthcare
              experiences. Clinics that strategically offer these services are creating
              stronger patient retention, differentiated positioning, and new recurring
              revenue opportunities.
            </p>
          </div>

          {/* CTA footer */}
          <div className="mt-5">
            <h2 className="font-display text-2xl font-extrabold tracking-tighter text-slate-900 leading-tight">
              Let's Build Your Next Growth Line{" "}
              <ArrowRight className="inline-block w-5 h-5 text-blue-700 -translate-y-0.5" />
            </h2>
            <p className="mt-1 text-[12px] text-slate-600">
              Turn-key support for modern primary care growth.
            </p>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <ContactBlock icon={Phone} label="Phone" value="(713) 725-7413" />
              <ContactBlock icon={Mail} label="Email" value="info@noderahealth.com" />
              <ContactBlock icon={Globe} label="Web" value="www.noderahealth.com" />
            </div>
          </div>

          {/* Compliance footer */}
          <div className="mt-5 pt-3 border-t border-slate-100 text-[8.5px] text-slate-400 leading-relaxed">
            Nodera Health provides operational infrastructure and business
            development services to licensed primary care and wellness clinics. We
            are not a healthcare provider, telehealth company, billing service, or
            pharmacy. Clinics retain full clinical, regulatory, and financial
            responsibility for the programs they offer. © {new Date().getFullYear()} Nodera Health.
          </div>
        </div>
      </div>

      {/* On-screen helper note (not printed) */}
      <div className="no-print rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900 leading-relaxed">
        <strong>Tip:</strong> In Chrome's print dialog, set Destination to{" "}
        <em>"Save as PDF"</em>, Layout to <em>Portrait</em>, Paper size to{" "}
        <em>US Letter</em>, and Margins to <em>"None"</em> for a pixel-perfect
        8.5×11 export with no header/footer chrome.
      </div>
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <div className="mt-1 mb-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
      {label}
    </div>
  );
}

function Divider() {
  return <div className="my-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />;
}

function ProgramCard({ icon: Icon, label, colSpan = 1 }) {
  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 ${
        colSpan === 3 ? "col-span-3" : ""
      }`}
    >
      <span className="inline-flex w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 items-center justify-center text-blue-700 flex-shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="text-[12px] font-semibold text-slate-800 leading-tight">{label}</span>
    </div>
  );
}

function ValueCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="flex items-start gap-2.5">
        <span className="inline-flex w-7 h-7 rounded-lg bg-slate-900 text-white items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div>
          <div className="font-display font-bold text-[12.5px] text-slate-900 leading-tight">
            {title}
          </div>
          <div className="mt-1 text-[11px] text-slate-600 leading-snug">{body}</div>
        </div>
      </div>
    </div>
  );
}

function ContactBlock({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-2.5">
      <div className="flex items-center gap-1.5 text-[8.5px] font-bold uppercase tracking-[0.18em] text-blue-700">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 text-[12px] font-semibold text-slate-900 break-all">{value}</div>
    </div>
  );
}
